import { useState, useEffect } from 'react';
import logger from '../../lib/logger';
import AdminLayout from '../../components/layout/AdminLayout';
import { eventosApi } from '../../lib/api';
import { Plus, Pencil, Trash2, Calendar, MapPin, Users, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const EventosPage = () => {
  const [eventos, setEventos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedEvento, setSelectedEvento] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    nome_evento: '',
    descricao: '',
    data_inicio: '',
    data_fim: '',
    local: '',
    capacidade_total: '',
    status: 'ativo',
  });

  useEffect(() => {
    fetchEventos();
  }, []);

  const fetchEventos = async () => {
    try {
      const response = await eventosApi.list();
      setEventos(response.data);
    } catch (error) {
      logger.error('Error fetching eventos:', error);
      toast.error('Erro ao carregar eventos');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      nome_evento: '',
      descricao: '',
      data_inicio: '',
      data_fim: '',
      local: '',
      capacidade_total: '',
      status: 'ativo',
    });
    setSelectedEvento(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEditDialog = (evento) => {
    setSelectedEvento(evento);
    setFormData({
      nome_evento: evento.nome_evento,
      descricao: evento.descricao || '',
      data_inicio: evento.data_inicio ? evento.data_inicio.slice(0, 16) : '',
      data_fim: evento.data_fim ? evento.data_fim.slice(0, 16) : '',
      local: evento.local,
      capacidade_total: evento.capacidade_total.toString(),
      status: evento.status,
    });
    setDialogOpen(true);
  };

  const openDeleteDialog = (evento) => {
    setSelectedEvento(evento);
    setDeleteDialogOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const payload = {
      ...formData,
      capacidade_total: parseInt(formData.capacidade_total),
      data_inicio: new Date(formData.data_inicio).toISOString(),
      data_fim: new Date(formData.data_fim).toISOString(),
    };

    try {
      if (selectedEvento) {
        await eventosApi.update(selectedEvento.id, payload);
        toast.success('Evento atualizado com sucesso!');
      } else {
        await eventosApi.create(payload);
        toast.success('Evento criado com sucesso!');
      }
      setDialogOpen(false);
      fetchEventos();
    } catch (error) {
      logger.error('Error saving evento:', error);
      toast.error(error.response?.data?.detail || 'Erro ao salvar evento');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedEvento) return;

    try {
      await eventosApi.delete(selectedEvento.id);
      toast.success('Evento deletado com sucesso!');
      setDeleteDialogOpen(false);
      fetchEventos();
    } catch (error) {
      logger.error('Error deleting evento:', error);
      toast.error('Erro ao deletar evento');
    }
  };

  const formatDate = (dateString) => {
    try {
      return format(new Date(dateString), "d 'de' MMM 'de' yyyy, HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };

  const getStatusBadge = (status) => {
    return status === 'ativo' 
      ? <span className="badge-success">Ativo</span>
      : <span className="badge-error">Encerrado</span>;
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-kairos-orange border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-8" data-testid="eventos-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-display text-3xl text-white mb-2">Eventos</h1>
            <p className="text-gray-400">Gerencie os eventos do Kairós</p>
          </div>
          <Button 
            onClick={openCreateDialog}
            className="bg-gradient-to-r from-kairos-orange to-kairos-orange-light text-white"
            data-testid="create-evento-btn"
          >
            <Plus className="w-5 h-5 mr-2" />
            Novo Evento
          </Button>
        </div>

        {/* Events Grid */}
        {eventos.length === 0 ? (
          <Card className="bg-obsidian-200 border-white/10">
            <CardContent className="py-16 text-center">
              <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400 mb-4">Nenhum evento cadastrado ainda.</p>
              <Button 
                onClick={openCreateDialog}
                className="bg-kairos-orange text-white"
              >
                Criar Primeiro Evento
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {eventos.map((evento) => (
              <Card 
                key={evento.id} 
                className="bg-obsidian-200 border-white/10 hover:border-kairos-orange/30 transition-colors"
                data-testid={`evento-card-${evento.id}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-white font-display text-xl">{evento.nome_evento}</CardTitle>
                    {getStatusBadge(evento.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  {evento.descricao && (
                    <p className="text-gray-400 text-sm line-clamp-2">{evento.descricao}</p>
                  )}
                  
                  <div className="space-y-2 text-sm">
                    <div className="flex items-center gap-2 text-gray-400">
                      <Calendar className="w-4 h-4 text-kairos-orange" />
                      <span>{formatDate(evento.data_inicio)}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <MapPin className="w-4 h-4 text-gray-400" />
                      <span>{evento.local}</span>
                    </div>
                    <div className="flex items-center gap-2 text-gray-400">
                      <Users className="w-4 h-4 text-green-500" />
                      <span>{evento.capacidade_total} vagas</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 pt-2 border-t border-white/10">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(evento)}
                      className="flex-1 text-gray-400 hover:text-white hover:bg-white/10"
                      data-testid={`edit-evento-${evento.id}`}
                    >
                      <Pencil className="w-4 h-4 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openDeleteDialog(evento)}
                      className="text-gray-400 hover:text-red-500 hover:bg-red-500/10"
                      data-testid={`delete-evento-${evento.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                    <a
                      href={`/evento/${evento.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 text-gray-400 hover:text-kairos-orange transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-obsidian-200 border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">
                {selectedEvento ? 'Editar Evento' : 'Novo Evento'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-300">Nome do Evento</Label>
                <Input
                  name="nome_evento"
                  value={formData.nome_evento}
                  onChange={handleInputChange}
                  required
                  className="mt-1 bg-obsidian-50 border-white/10 text-white"
                  placeholder="Ex: Encontro de Casais 2024"
                  data-testid="evento-nome-input"
                />
              </div>

              <div>
                <Label className="text-gray-300">Descrição</Label>
                <Textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  className="mt-1 bg-obsidian-50 border-white/10 text-white min-h-[80px]"
                  placeholder="Descrição do evento..."
                  data-testid="evento-descricao-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Data Início</Label>
                  <Input
                    type="datetime-local"
                    name="data_inicio"
                    value={formData.data_inicio}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-obsidian-50 border-white/10 text-white"
                    data-testid="evento-data-inicio-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Data Fim</Label>
                  <Input
                    type="datetime-local"
                    name="data_fim"
                    value={formData.data_fim}
                    onChange={handleInputChange}
                    required
                    className="mt-1 bg-obsidian-50 border-white/10 text-white"
                    data-testid="evento-data-fim-input"
                  />
                </div>
              </div>

              <div>
                <Label className="text-gray-300">Local</Label>
                <Input
                  name="local"
                  value={formData.local}
                  onChange={handleInputChange}
                  required
                  className="mt-1 bg-obsidian-50 border-white/10 text-white"
                  placeholder="Ex: Centro de Convenções"
                  data-testid="evento-local-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-300">Capacidade Total</Label>
                  <Input
                    type="number"
                    name="capacidade_total"
                    value={formData.capacidade_total}
                    onChange={handleInputChange}
                    required
                    min="1"
                    className="mt-1 bg-obsidian-50 border-white/10 text-white"
                    placeholder="100"
                    data-testid="evento-capacidade-input"
                  />
                </div>
                <div>
                  <Label className="text-gray-300">Status</Label>
                  <Select 
                    value={formData.status} 
                    onValueChange={(value) => setFormData(prev => ({ ...prev, status: value }))}
                  >
                    <SelectTrigger className="mt-1 bg-obsidian-50 border-white/10 text-white" data-testid="evento-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-obsidian-200 border-white/10">
                      <SelectItem value="ativo" className="text-white hover:bg-white/10">Ativo</SelectItem>
                      <SelectItem value="encerrado" className="text-white hover:bg-white/10">Encerrado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <DialogFooter>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-kairos-orange text-white hover:bg-kairos-orange-light"
                  data-testid="save-evento-btn"
                >
                  {submitting ? 'Salvando...' : 'Salvar'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-obsidian-200 border-white/10">
            <AlertDialogHeader>
              <AlertDialogTitle className="text-white">Deletar Evento?</AlertDialogTitle>
              <AlertDialogDescription className="text-gray-400">
                Esta ação não pode ser desfeita. O evento "{selectedEvento?.nome_evento}" e todos os lotes associados serão permanentemente removidos.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel className="border-white/20 text-white hover:bg-white/10">
                Cancelar
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 text-white hover:bg-red-700"
                data-testid="confirm-delete-evento"
              >
                Deletar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default EventosPage;
